import React, { Suspense, lazy, useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { Loader2, MapPin, MailPlus, KanbanSquare, ArrowRight } from 'lucide-react';
import { supabase, isConfigured } from './services/supabase';
import type { AppView } from './types';
import { APP_TAGLINE } from './constants';

import FluidBackground from './components/FluidBackground';
import Header from './components/Header';
import Footer from './components/Footer';
import Login from './components/Login';
import ProspectScreen from './components/ProspectScreen';
import CampaignsScreen from './components/CampaignsScreen';
import CampaignDetail from './components/CampaignDetail';
import PipelineScreen from './components/PipelineScreen';
import SettingsScreen from './components/SettingsScreen';
import PricingScreen from './components/PricingScreen';
import PublicLandingPage from './components/PublicLandingPage';

// LetterDesigner pulls in @react-pdf/renderer (~1.5MB), keep it out of the main bundle.
const LetterDesigner = lazy(() => import('./components/LetterDesigner'));

interface LandingRoute {
  slug: string;
  uprn: string | null;
}

function getLandingRoute(): LandingRoute | null {
  const m = window.location.pathname.match(/^\/l\/([^/?#]+)/);
  if (!m) return null;
  const params = new URLSearchParams(window.location.search);
  return { slug: m[1], uprn: params.get('u') };
}

function UnionJack({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 60 30"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-label="United Kingdom"
      role="img"
    >
      <clipPath id="uk-jack-clip">
        <path d="M0,0 v30 h60 v-30 z" />
      </clipPath>
      <clipPath id="uk-jack-stclip">
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
      </clipPath>
      <g clipPath="url(#uk-jack-clip)">
        <rect width="60" height="30" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path
          d="M0,0 L60,30 M60,0 L0,30"
          clipPath="url(#uk-jack-stclip)"
          stroke="#C8102E"
          strokeWidth="4"
        />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
      </g>
    </svg>
  );
}

function LoginLanding({ onAuthSuccess }: { onAuthSuccess: () => void }) {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex flex-col bg-black relative overflow-hidden">
      <FluidBackground />
      <div className="relative z-10 flex flex-col flex-1 min-h-0 overflow-y-auto">
        <Header
          user={null}
          onLogin={() => { setAuthMode('login'); setShowLoginModal(true); }}
          onSignUp={() => { setAuthMode('signup'); setShowLoginModal(true); }}
        />

        <main className="flex-1">
          <div className="container mx-auto px-4 py-16">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-900/30 backdrop-blur-sm border border-emerald-500/20 text-emerald-300 rounded-full text-sm font-medium mb-8">
                <UnionJack className="w-4 h-auto shrink-0 rounded-sm shadow-sm ring-1 ring-white/10" />
                Prospecting for UK Estate Agents
              </div>

              <div className="flex justify-center mb-6">
                <img
                  src="/Logo.png"
                  alt="Doorsteps by ReHub"
                  className="h-8 md:h-12 w-auto object-contain drop-shadow-[0_0_24px_rgba(16,185,129,0.25)]"
                />
              </div>

              <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
                {APP_TAGLINE}
              </p>

              <div className="flex flex-wrap items-center justify-center gap-4 mb-16">
                <button
                  onClick={() => { setAuthMode('signup'); setShowLoginModal(true); }}
                  className="group flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-2xl font-semibold text-lg transition-all shadow-xl shadow-emerald-900/40 hover:shadow-emerald-500/30 hover:scale-105 border border-emerald-500/30"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-700 group-hover:rotate-[360deg] transition-transform duration-700">
                    <MapPin size={20} strokeWidth={2} />
                  </span>
                  Try Doorsteps Free
                </button>
                <button
                  onClick={() => { setAuthMode('login'); setShowLoginModal(true); }}
                  className="px-8 py-5 bg-slate-900/80 backdrop-blur-sm border-2 border-slate-700 hover:border-slate-500 text-slate-200 hover:text-white rounded-2xl font-semibold text-lg transition-all shadow-lg"
                >
                  Sign In
                </button>
              </div>

              {/* Feature trio */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
                <FeatureCard
                  icon={<MapPin size={22} />}
                  title="Pinpoint likely sellers"
                  body="Move-likelihood scoring from Land Registry + EPC data. Draw a territory, see who to write to."
                />
                <FeatureCard
                  icon={<MailPlus size={22} />}
                  title="Branded direct mail"
                  body="One designed letter template, your branding, posted via Stannp. QR code links to your valuation page."
                />
                <FeatureCard
                  icon={<KanbanSquare size={22} />}
                  title="Track every lead"
                  body="From QR scan to instructed. Drag cards through a 5-stage pipeline. Activity log per homeowner."
                />
              </div>

              <div className="mt-16 text-xs text-slate-500 flex items-center justify-center gap-2">
                <span>Already an Essex / London / Kent agent?</span>
                <button
                  onClick={() => { setAuthMode('signup'); setShowLoginModal(true); }}
                  className="text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
                >
                  Get started <ArrowRight size={12} />
                </button>
              </div>
            </div>
          </div>
        </main>

        <Footer />

        {showLoginModal && (
          <div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowLoginModal(false)}
          >
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md">
              <Login
                onAuthSuccess={() => { setShowLoginModal(false); onAuthSuccess(); }}
                onClose={() => setShowLoginModal(false)}
                initialMode={authMode}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="text-left p-5 bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl hover:border-emerald-500/30 transition-colors">
      <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center mb-3">
        {icon}
      </div>
      <h3 className="text-white font-semibold text-sm mb-1">{title}</h3>
      <p className="text-slate-400 text-xs leading-relaxed">{body}</p>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<AppView>('LOADING');
  const [activeCampaignId, setActiveCampaignId] = useState<string | null>(null);

  const landing = getLandingRoute();

  useEffect(() => {
    if (landing) {
      setLoading(false);
      return;
    }
    if (!isConfigured) {
      setLoading(false);
      setView('LOGIN');
      return;
    }
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setUser(data.session?.user ?? null);
      setView(data.session?.user ? 'PROSPECT' : 'LOGIN');
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_, s) => {
      setSession(s ?? null);
      setUser(s?.user ?? null);
      setView((curr) => {
        if (s?.user && curr === 'LOGIN') return 'PROSPECT';
        if (!s?.user) return 'LOGIN';
        return curr;
      });
    });
    return () => sub.subscription.unsubscribe();
  }, [landing]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setView('LOGIN');
  };

  if (landing) {
    return <PublicLandingPage slug={landing.slug} uprn={landing.uprn} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-emerald-500">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (!user || view === 'LOGIN') {
    return <LoginLanding onAuthSuccess={() => setView('PROSPECT')} />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header
        user={user}
        view={view}
        onNavigate={(v) => {
          setView(v);
          if (v !== 'CAMPAIGN_DETAIL') setActiveCampaignId(null);
        }}
        onSignOut={handleSignOut}
      />

      <main className="flex-1 min-h-0 flex flex-col">
        {view === 'PROSPECT' && (
          <ProspectScreen
            user={user}
            onCampaignCreated={(id) => {
              setActiveCampaignId(id);
              setView('CAMPAIGN_DETAIL');
            }}
            onUpgradeRequested={() => setView('PRICING')}
          />
        )}
        {view === 'CAMPAIGNS' && (
          <CampaignsScreen
            user={user}
            onOpenCampaign={(id) => {
              setActiveCampaignId(id);
              setView('CAMPAIGN_DETAIL');
            }}
          />
        )}
        {view === 'CAMPAIGN_DETAIL' && activeCampaignId && (
          <CampaignDetail
            user={user}
            campaignId={activeCampaignId}
            onBack={() => setView('CAMPAIGNS')}
            onOpenDesigner={() => setView('LETTER_DESIGNER')}
            onUpgradeRequested={() => setView('PRICING')}
          />
        )}
        {view === 'LETTER_DESIGNER' && activeCampaignId && (
          <Suspense
            fallback={
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <Loader2 className="animate-spin" size={28} />
              </div>
            }
          >
            <LetterDesigner
              user={user}
              mode="campaign"
              campaignId={activeCampaignId}
              onBack={() => setView('CAMPAIGN_DETAIL')}
            />
          </Suspense>
        )}
        {view === 'DESIGN' && (
          <Suspense
            fallback={
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <Loader2 className="animate-spin" size={28} />
              </div>
            }
          >
            <LetterDesigner
              user={user}
              mode="defaults"
              onBack={() => setView('PROSPECT')}
            />
          </Suspense>
        )}
        {view === 'PIPELINE' && <PipelineScreen user={user} />}
        {view === 'PRICING' && <PricingScreen user={user} />}
        {view === 'SETTINGS' && <SettingsScreen user={user} />}
      </main>
    </div>
  );
}
